import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, FileDown, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { downloadUrl, formatApiError } from "../../lib/api";

const OVERDUE_DAYS = 14;
const isOverdue = (inv) => inv.status !== "paid" && (Date.now() - new Date(inv.created_at).getTime()) / 86400000 > OVERDUE_DAYS;

export default function Statements() {
  const [invoices, setInvoices] = useState(null);

  useEffect(() => {
    api.get("/my/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; api is a stable module import
  }, []);

  const months = useMemo(() => {
    const map = new Map();
    for (const inv of invoices || []) {
      const key = inv.created_at.slice(0, 7);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(inv);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [invoices]);

  const monthTotals = (list) => {
    const t = {};
    for (const inv of list) {
      const c = t[inv.currency] || { paid: 0, due: 0 };
      if (inv.status === "paid") c.paid += inv.amount;
      else c.due += inv.amount;
      t[inv.currency] = c;
    }
    return Object.entries(t);
  };

  if (invoices === null) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" /></div>;
  }

  return (
    <div data-testid="statements-page">
      <h1 className="font-display font-black text-3xl tracking-tight mb-2">Statements</h1>
      <p className="text-sm text-slate-500 mb-8">All your invoices and receipts, month by month.</p>

      {invoices.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-20 text-center" data-testid="statements-empty-state">
          <Receipt className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <p className="font-display font-bold text-xl">No invoices yet</p>
          <p className="text-sm text-slate-500 mt-2">Invoices for your cases will appear here once issued.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {months.map(([month, list]) => (
            <section key={month} data-testid={`statement-month-${month}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h2 className="font-display font-bold text-lg">
                  {new Date(month + "-02").toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                </h2>
                <div className="flex flex-wrap gap-2 text-xs font-bold">
                  {monthTotals(list).map(([cur, t]) => (
                    <span key={cur} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1">
                      {t.paid > 0 && <span className="text-emerald-600">{cur} {t.paid.toFixed(2)} paid</span>}
                      {t.due > 0 && <span className="text-amber-600">{t.paid > 0 ? "· " : ""}{cur} {t.due.toFixed(2)} due</span>}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <ul className="divide-y divide-slate-100">
                  {list.map((inv) => {
                    const overdue = isOverdue(inv);
                    return (
                      <li key={inv.id} className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${overdue ? "bg-red-50/50" : ""}`} data-testid={`statement-row-${inv.case_ref}`}>
                        <div className="flex items-center gap-4 min-w-0">
                          <Link to={`/portal/cases/${inv.case_id}`} className="font-mono font-semibold text-sky-700 hover:underline text-sm" data-testid={`statement-case-link-${inv.case_ref}`}>
                            {inv.case_ref}
                          </Link>
                          <span className="font-bold text-sm">{inv.currency} {Number(inv.amount).toFixed(2)}</span>
                          {inv.status === "paid" ? (
                            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                              Paid{inv.payment_method ? ` · ${inv.payment_method}` : ""}
                            </span>
                          ) : overdue ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-red-600" data-testid={`statement-overdue-${inv.case_ref}`}>
                              <AlertTriangle className="h-3 w-3" /> Overdue
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-700">
                              Unpaid
                            </span>
                          )}
                          <span className="text-xs text-slate-400">
                            Issued {new Date(inv.created_at).toLocaleDateString()}{inv.paid_at ? ` · paid ${new Date(inv.paid_at).toLocaleDateString()}` : ""}
                          </span>
                        </div>
                        {inv.status === "paid" ? (
                          <button onClick={() => downloadUrl(`/invoices/${inv.id}/receipt`, `receipt-${inv.case_ref}.pdf`).catch((e) => toast.error(formatApiError(e)))}
                            data-testid={`statement-receipt-${inv.case_ref}`}
                            className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-800">
                            <FileDown className="h-3.5 w-3.5" /> Receipt
                          </button>
                        ) : (
                          <Link to={`/portal/cases/${inv.case_id}`} data-testid={`statement-pay-${inv.case_ref}`}
                            className="inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-800">
                            Pay now <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
